import { ChangeDetectionStrategy, Component, signal } from '@angular/core';

// Shape shared by UserImage and CustomizationOption - the picker only needs an id, a
// display name, an optional description, and an image path, so it works for all three
// cosmetic categories without caring which concrete DTO it's handed.
export interface CustomizationPickerItem {
    Id: string;
    Name: string;
    Description?: string;
    ImagePath: string;
}

// Shared "owned options only, tap to equip immediately" picker for profile images, profile
// backgrounds, and pet backgrounds. No Save/Apply button - selecting a tile equips it right
// away; the caller's onSelect callback is responsible for the optimistic-update/revert-on-
// error flow (see services/customization/*).
@Component({
    selector: 'app-customization-picker',
    templateUrl: './customization-picker.component.html',
    styleUrls: ['./customization-picker.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomizationPickerComponent {

    // Every opener (profile.component.ts, profile-pet.component.ts) configures a freshly-opened
    // instance of this component imperatively via GenericModalComponent's callback, not through
    // @Input() bindings - items specifically arrives asynchronously (a subscribe callback on the
    // opener's side), so it must be a signal for OnPush to see the update. Public because each
    // opener genuinely needs to write it as part of that same configuration callback.
    public items = signal<CustomizationPickerItem[]>([]);
    public equippedId = signal<string | null>(null);

    // Backgrounds can always fall back to "nothing equipped" (the blue-red-gradient banner
    // shows through) - profile images can't, since every account owns the guaranteed
    // "Default" image and there's nothing sensible to fall back to instead. Callers that want
    // the "none" tile set allowDefault; callers that also want it to preview as a real swatch
    // (rather than bare text) also set defaultPreviewClass to a CSS class that paints one -
    // e.g. 'blue-red-gradient', the same class the profile banner itself uses.
    public allowDefault = true;
    public defaultLabel = 'Default';
    public defaultPreviewClass: string | null = null;

    // Profile images render as circular thumbnails (matching how the equipped image renders
    // everywhere else in the app); backgrounds render as their real rectangular aspect ratio,
    // since that's how they'll actually look once equipped.
    public roundImages = false;

    public onSelect: (item: CustomizationPickerItem | null) => void = () => { };

    public select(item: CustomizationPickerItem | null): void {
        this.equippedId.set(item?.Id ?? null);
        this.onSelect(item);
    }
}
